import React, { useMemo, useRef, useState, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { useLanguage } from "../../../contexts/LanguageContext";

const ExternalVehicleTable = ({
  rows = [],
  loading = false,
  filters,
  totalCount = 0,
  pagination = { PageNumber: 1, PageSize: 10 },
  onPageChange = () => { },
  handleEdit = () => { },
  handleDelete = () => { }
}) => {
  const { th } = useLanguage();
  const dataTableRef = useRef(null);
  const columnsRef = useRef(null);

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(0);

  const getColumnsCallback = useCallback((el) => {
    if (el && el.getColumns && !columnsRef.current) columnsRef.current = el.getColumns();
    dataTableRef.current = el;
  }, []);

  const confirmDelete = (row) => {
    confirmDialog({
      message: th("externalVehicle.confirm.deleteMessage"),
      header: th("externalVehicle.confirm.deleteHeader"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: th("common.yes"),
      rejectLabel: th("common.no"),
      accept: () => handleDelete(row),
      rejectClassName: "p-button-outlined"
    });
  };

  const actionBodyTemplate = (row) => (
    <div className="flex items-center gap-3">
      <i
        onClick={() => handleEdit(row)}
        className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700"
        title={th("common.edit")}
      />
      <i
        onClick={() => confirmDelete(row)}
        className="pi pi-trash cursor-pointer text-red-500 hover:text-red-700"
        title={th("common.delete")}
      />
    </div>
  );

  const dateTemplate = (rowData, field) => {
    if (!rowData[field]) return "-";
    const date = new Date(rowData[field]);
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const normalized = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const sorted = useMemo(() => {
    if (!sortField || sortOrder === 0) return normalized;

    const copy = [...normalized];

    return copy.sort((a, b) => {
      let av = a?.[sortField];
      let bv = b?.[sortField];

      if (av == null && bv == null) return 0;
      if (av == null) return sortOrder;
      if (bv == null) return -sortOrder;

      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();

      if (av < bv) return -sortOrder;
      if (av > bv) return sortOrder;
      return 0;
    });
  }, [normalized, sortField, sortOrder]);

  const handleSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
  };

  return (
    <>
      <ConfirmDialog />
      <DataTable
        key={`vehicle-table-${sorted.length}-${loading}-${sortField}-${sortOrder}`}
        value={sorted}
        dataKey="id"
        lazy
        paginator
        first={(pagination?.PageNumber - 1) * (pagination?.PageSize || 10)}
        rows={pagination?.PageSize || 10}
        totalRecords={totalCount}
        onPage={onPageChange}
        onSort={handleSort}
        sortField={sortField}
        sortOrder={sortOrder}
        loading={loading}
        filters={filters}
        filterDisplay="menu"
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        tableStyle={{ minWidth: "50rem" }}
        showGridlines
        stripedRows
        sortMode="single"
        ref={getColumnsCallback}
        emptyMessage={th("externalVehicle.empty")}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={th("table.paginatorTemplate")}
      >
        <Column
          body={actionBodyTemplate}
          exportable={false}
          style={{ width: "5%", minWidth: "80px" }}
        />

        <Column
          field="brand"
          header={th("externalVehicle.brand")}
          sortable
          style={{ minWidth: "150px" }}
        />
        <Column
          field="model"
          header={th("externalVehicle.model")}
          sortable
          style={{ minWidth: "150px" }}
        />
        <Column
          field="chassisNo"
          header={th("externalVehicle.chassisNo")}
          sortable
          style={{ minWidth: "200px" }}
        />
        <Column
          field="licensePlate"
        header={th("externalVehicle.licensePlate")}
          sortable
          style={{ minWidth: "150px" }}
        />
        <Column
          field="createdDate"
          header={th("common.createdDate")}
          body={(rowData) => dateTemplate(rowData, "createdDate")}
          sortable
          style={{ minWidth: "150px" }}
        />
        <Column
          field="createdName"
          header={th("common.created")}
          sortable
          style={{ minWidth: "150px" }}
        />
        <Column
          field="modifiedDate"
          header={th("common.modifiedDate")}
          body={(rowData) => dateTemplate(rowData, "modifiedDate")}
          sortable
          style={{ minWidth: "150px" }}
        />
        <Column
          field="modifiedName"
          header={th("common.modified")}
          sortable
          style={{ minWidth: "150px" }}
        />
      </DataTable>
    </>
  );
};

export default ExternalVehicleTable;
